import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cashAPI, getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { CircleDollarSign, Play, Square, RefreshCw, ArrowRight } from 'lucide-react';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { useLanguage } from '@/i18n';

export default function SalesCashIntegrationPanel({
  selectedLocation,
  activeSession,
  loadingSession,
  canManageCash,
  onRefreshSession,
  autoOpenSessionRequested = false,
  onAutoOpenSessionHandled,
}) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openingBalance, setOpeningBalance] = useState('');
  const [openingNotes, setOpeningNotes] = useState('');
  const [closingBalance, setClosingBalance] = useState('');
  const [differenceJustification, setDifferenceJustification] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const cashCenterTargetUrl = selectedLocation?.id
    ? `/cash-management?location_id=${encodeURIComponent(selectedLocation.id)}`
    : '/cash-management';

  useEffect(() => {
    if (!autoOpenSessionRequested) return;

    if (canManageCash && selectedLocation?.id && !activeSession) {
      setOpenDialogOpen(true);
    }

    onAutoOpenSessionHandled?.();
  }, [autoOpenSessionRequested, canManageCash, selectedLocation?.id, activeSession, onAutoOpenSessionHandled]);

  const handleOpenSession = async () => {
    if (!selectedLocation?.id) {
      toast.error(t('salesCashPanel.selectLocationError'));
      return;
    }
    const parsedBalance = parseFloat(openingBalance || '0');
    if (!Number.isFinite(parsedBalance) || parsedBalance <= 0) {
      toast.error(t('salesCashPanel.invalidOpeningBalance'));
      return;
    }

    setSubmitting(true);
    try {
      await cashAPI.openSession({
        location_id: selectedLocation.id,
        opening_balance: parsedBalance,
        notes: openingNotes?.trim() || undefined,
      });
      toast.success(t('salesCashPanel.sessionOpened'));
      setOpenDialogOpen(false);
      setOpeningBalance('');
      setOpeningNotes('');
      await onRefreshSession?.();
    } catch (error) {
      toast.error(getErrorMessage(error, t('salesCashPanel.openError')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSession = async () => {
    if (!activeSession?.id) {
      toast.error(t('salesCashPanel.noActiveSession'));
      return;
    }
    const parsedBalance = parseFloat(closingBalance || '0');
    if (!Number.isFinite(parsedBalance) || parsedBalance < 0) {
      toast.error(t('salesCashPanel.invalidClosingBalance'));
      return;
    }

    setSubmitting(true);
    try {
      await cashAPI.closeSession(activeSession.id, {
        closing_balance: parsedBalance,
        difference_justification: differenceJustification?.trim() || undefined,
        notes: closingNotes?.trim() || undefined,
      });
      toast.success(t('salesCashPanel.sessionClosed'));
      setCloseDialogOpen(false);
      setClosingBalance('');
      setDifferenceJustification('');
      setClosingNotes('');
      await onRefreshSession?.();
    } catch (error) {
      toast.error(getErrorMessage(error, t('salesCashPanel.closeError')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card data-testid="sales-cash-integration-panel">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <CircleDollarSign className="h-4 w-4 text-emerald-600" />
            {t('salesCashPanel.title')}
          </CardTitle>
          <CardDescription>
            {t('salesCashPanel.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2" data-testid="sales-cash-session-status-row">
            <Badge variant={activeSession ? 'default' : 'outline'} data-testid="sales-cash-session-status-badge">
              {activeSession ? t('salesCashPanel.sessionOpen') : t('salesCashPanel.sessionClosedLabel')}
            </Badge>
            <span className="text-sm text-muted-foreground" data-testid="sales-cash-session-location-label">
              {selectedLocation?.name || t('salesCashPanel.noLocationSelected')}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRefreshSession}
              disabled={loadingSession}
              data-testid="sales-cash-refresh-session-button"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {activeSession ? (
            <div className="grid gap-3 rounded-xl border bg-slate-50 p-3 sm:grid-cols-3" data-testid="sales-cash-session-summary-grid">
              <div>
                <p className="text-xs uppercase text-muted-foreground">{t('labels.code')}</p>
                <p className="font-medium" data-testid="sales-cash-session-code-value">{activeSession.session_code || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-muted-foreground">{t('salesCashPanel.openingBalance')}</p>
                <p className="font-medium" data-testid="sales-cash-session-opening-balance-value">
                  {formatCurrency(activeSession.opening_balance || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-muted-foreground">{t('salesCashPanel.sessionSales')}</p>
                <p className="font-medium text-emerald-700" data-testid="sales-cash-session-sales-total-value">
                  {formatCurrency(activeSession.total_sales || 0)}
                </p>
              </div>
              <div className="sm:col-span-3">
                <p className="text-xs uppercase text-muted-foreground">{t('salesCashPanel.openedAt')}</p>
                <p className="text-sm" data-testid="sales-cash-session-opened-at-value">{formatDateTime(activeSession.opened_at)}</p>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="sales-cash-session-empty-state">
              {t('salesCashPanel.noSessionForLocation')}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {canManageCash && !activeSession && (
              <Button type="button" onClick={() => setOpenDialogOpen(true)} data-testid="sales-cash-open-session-button">
                <Play className="mr-2 h-4 w-4" />
                {t('salesCashPanel.openIntegratedCash')}
              </Button>
            )}
            {canManageCash && activeSession && (
              <Button type="button" variant="destructive" onClick={() => setCloseDialogOpen(true)} data-testid="sales-cash-close-session-button">
                <Square className="mr-2 h-4 w-4" />
                {t('salesCashPanel.closeCash')}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(cashCenterTargetUrl)}
              data-testid="sales-cash-open-advanced-page-button"
            >
              {t('salesCashPanel.openCashCenter')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={openDialogOpen} onOpenChange={setOpenDialogOpen}>
        <DialogContent data-testid="sales-cash-open-session-dialog">
          <DialogHeader>
            <DialogTitle>{t('salesCashPanel.openDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('salesCashPanel.openDialogDescription', { location: selectedLocation?.name || t('salesCashPanel.selectedLocationFallback') })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('salesCashPanel.openingBalance')} *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={openingBalance}
                onChange={(event) => setOpeningBalance(event.target.value)}
                data-testid="sales-cash-open-balance-input"
              />
            </div>
            <div>
              <Label>{t('labels.notes')}</Label>
              <Textarea
                value={openingNotes}
                onChange={(event) => setOpeningNotes(event.target.value)}
                rows={2}
                data-testid="sales-cash-open-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpenDialogOpen(false)} data-testid="sales-cash-open-cancel-button">
              {t('actions.cancel')}
            </Button>
            <Button type="button" onClick={handleOpenSession} disabled={submitting} data-testid="sales-cash-open-confirm-button">
              {submitting ? t('salesCashPanel.opening') : t('salesCashPanel.confirmOpen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent data-testid="sales-cash-close-session-dialog">
          <DialogHeader>
            <DialogTitle>{t('salesCashPanel.closeDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('salesCashPanel.closeDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('salesCashPanel.closingBalance')} *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={closingBalance}
                onChange={(event) => setClosingBalance(event.target.value)}
                data-testid="sales-cash-close-balance-input"
              />
            </div>
            <div>
              <Label>{t('salesCashPanel.differenceJustification')}</Label>
              <Textarea
                value={differenceJustification}
                onChange={(event) => setDifferenceJustification(event.target.value)}
                rows={2}
                data-testid="sales-cash-close-difference-justification-input"
              />
            </div>
            <div>
              <Label>{t('salesCashPanel.closingNotes')}</Label>
              <Textarea
                value={closingNotes}
                onChange={(event) => setClosingNotes(event.target.value)}
                rows={2}
                data-testid="sales-cash-close-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCloseDialogOpen(false)} data-testid="sales-cash-close-cancel-button">
              {t('actions.cancel')}
            </Button>
            <Button type="button" variant="destructive" onClick={handleCloseSession} disabled={submitting} data-testid="sales-cash-close-confirm-button">
              {submitting ? t('salesCashPanel.closing') : t('salesCashPanel.confirmClose')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}